import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { QuestionWithOptionsService } from '@core/ask-hr-dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormData,
  UnwrapSignal,
} from '@core/ask-hr-dynamic-forms-types';
import type { SelectTemplateItem } from '@core/ask-hr-templates-types';
import { I18nService } from '@core/i18n-legacy';

interface RespondConfig {
  question: string;
  hint: string;
  required: boolean;
  options: Array<{ value: string }>;
}

type RespondComponentType =
  import('@core/ask-hr-dynamic-forms-smart-ui').RespondSelectSingleComponent<
    RespondConfig['options'][number]['value']
  >;

export class SelectSingleConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionWithOptionsService = inject(QuestionWithOptionsService);
  private readonly i18n = inject(I18nService);

  public readonly kind: SelectTemplateItem['type'] = 'select';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.single_choice'),
    icon: 'done',
  };

  public readonly components = {
    define: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.QuestionWithOptionsComponent),
    respond: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.RespondSelectSingleComponent),
    present: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (data?: DefineFormData) =>
    this.questionWithOptionsService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondConfig,
  ): UnwrapSignal<RespondComponentType['respondConfig']> => ({ ...data, kind: 'radio' });

  public readonly respondForm = (
    data: RespondFormData,
  ): UnwrapSignal<RespondComponentType['respondForm']> =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>(
        null,
        data.required ? Validators.required : [],
      ),
    });

  public readonly presentConfig = (
    data: SelectTemplateItem,
  ): UnwrapSignal<
    import('@core/ask-hr-dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
